import { cssThemedValue } from '@creditornot/cb-ingredients';
import React, { PropsWithChildren, ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { Typography } from 'common/typography';
import { useCampaignModuleContext } from 'modules/campaigns/context';

import { UNLIMITED_TOTAL_BUDGET_SUM } from '../budget-section/constants';
import { getDuration } from '../scheduling-section/utils';

export type EstimationsProps = {
  totalBudgetSum: number | undefined;
  startDate: string;
  endDate: string;
  timezone: string;
  firstPurchaseOnly: boolean;
  allowUnlimitedBudget?: boolean;
};

const StyledRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StyledTitle = styled(Typography.Heading6)`
  margin: 0 16px;
`;

const StyledRow = styled.div`
  display: flex;
  gap: 8px;
  justify-content: space-between;
  padding: 16px;

  &:not(:last-child) {
    border-block-end: 1px solid ${cssThemedValue('borderPrimary')};
  }
`;

const EstimationRow = ({ label, children }: PropsWithChildren<{ label: ReactNode }>) => (
  <StyledRow>
    <Typography.Body3>{label}</Typography.Body3>
    <Typography.Body3 fontWeight="bold">{children}</Typography.Body3>
  </StyledRow>
);

export const Estimations = ({
  totalBudgetSum,
  startDate,
  endDate,
  timezone,
  firstPurchaseOnly,
  allowUnlimitedBudget = true,
}: EstimationsProps) => {
  const { currency } = useCampaignModuleContext();
  const { locale } = useIntl();
  const duration = getDuration(startDate, endDate, timezone);

  return (
    <StyledRoot>
      <StyledTitle>
        <FormattedMessage id="campaigns.sidebar-preview.summary.title" />
      </StyledTitle>

      <div>
        {totalBudgetSum !== undefined && (
          <EstimationRow label={<FormattedMessage id="campaigns.sidebar-preview.summary.budget" />}>
            {totalBudgetSum === UNLIMITED_TOTAL_BUDGET_SUM && allowUnlimitedBudget ? (
              <FormattedMessage id="campaigns.form.budget.unlimited-budget" />
            ) : (
              <MonetaryAmount amount={totalBudgetSum} currency={currency} />
            )}
          </EstimationRow>
        )}
        <EstimationRow label={<FormattedMessage id="campaigns.sidebar-preview.summary.audience" />}>
          {firstPurchaseOnly ? (
            <FormattedMessage id="campaigns.form.audience.first-time" />
          ) : (
            <FormattedMessage id="campaigns.form.audience.everybody" />
          )}
        </EstimationRow>
        <EstimationRow label={<FormattedMessage id="campaigns.sidebar-preview.summary.duration" />}>
          {duration.value?.toLocaleString(locale, {
            style: 'unit',
            unit: duration.unit,
            unitDisplay: 'long',
          })}
        </EstimationRow>
      </div>
    </StyledRoot>
  );
};
